import React, { useEffect } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { Helmet } from 'react-helmet'
import { getSrc, getSrcSet } from 'gatsby-plugin-image'

// Icon
import favicon from 'img/favicon.png'

const removeHTML = (content: string) => {
  let newContent = content

  if (typeof document !== 'undefined') {
    const titleHtml = newContent
    const div = document.createElement('div')
    div.innerHTML = titleHtml
    newContent = div.innerText
  }

  return newContent
}

interface SEOProps {
  seo: any
  dateGmt?: string
  info?: any
  location?: any
  lcpImage?: any
}

const SEO: React.FC<SEOProps> = ({ seo, dateGmt, info, location, lcpImage }) => {
  const {
    site,
    wpComponent,
    // eslint-disable-next-line
  } = useStaticQuery<GatsbyTypes.socialSharingQueryQuery>(graphql`
    query socialSharingQuery {
      site {
        siteMetadata {
          siteUrl
        }
      }
      wpComponent(databaseId: { eq: 204 }) {
        socialSharing {
          image {
            localFile {
              publicURL
            }
          }
        }
      }
    }
  `)

  const defaultImageUrl = wpComponent?.socialSharing?.image
    ? `${site?.siteMetadata?.siteUrl}${wpComponent?.socialSharing?.image?.localFile?.publicURL}`
    : ''

  const image: any = seo.opengraphImage
    ? seo.opengraphImage.sourceUrl
    : defaultImageUrl

  const lcpImageData = lcpImage?.localFile?.childImageSharp?.gatsbyImageData
  const lcpPreloadSrc = lcpImageData ? getSrc(lcpImageData) : null
  const lcpPreloadSrcSet = lcpImageData ? getSrcSet(lcpImageData) : null

  // add structure data JobPosting here

  if (location && location.pathname?.includes('/vacatures/')) {
    useEffect(() => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'

      const jobPosting = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        title: seo.title ? removeHTML(seo.title) : '',
        description: seo.metaDesc ? removeHTML(seo.metaDesc) : '',
        datePosted: dateGmt || new Date().toISOString().split('T')[0],
        hiringOrganization: {
          '@type': 'Organization',
          name: 'Unit14 Beveiliging',
          sameAs: site?.siteMetadata?.siteUrl || '',
          logo: `${site?.siteMetadata?.siteUrl}/img/logo-unit14.png`,
        },
        jobLocation: {
          '@type': 'Place',
          address: [
            {
              '@type': 'PostalAddress',
              addressRegion: 'NL-UT',
              streetAddress: 'Neerduist 5-F',
              addressLocality: 'Bunschoten-Spakenburg',
              postalCode: '3751 LX',
              addressCountry: 'NL',
            },
            {
              '@type': 'PostalAddress',
              addressRegion: 'NL-UT',
              streetAddress: 'Marathon 11-F',
              addressLocality: 'Hilversum',
              postalCode: '1213 PG',
              addressCountry: 'NL',
            },
          ],
        },
        employmentType: 'FULL_TIME',
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'EUR',
          value: {
            '@type': 'QuantitativeValue',
            value: `${info.usps[3]?.text.replace('p.m.', '').replace('.', '')}`,
            unitText: 'MONTH',
          },
        },
      }

      script.text = JSON.stringify(jobPosting)
      document.head.appendChild(script)

      return () => {
        document.head.removeChild(script)
      }
    }, [seo, site])
  }

  return (
    <Helmet
      htmlAttributes={{
        lang: 'nl',
      }}
      title={removeHTML(seo.title)}
    >
      {/* Default */}
      {seo.metaDesc && (
        <meta name="description" content={removeHTML(seo.metaDesc)} />
      )}
      {seo.canonical && (
        <link rel="canonical" href={removeHTML(seo.canonical)} />
      )}
      <meta name="image" content={image} />
      <link rel="icon" href={favicon} />
      {lcpPreloadSrc && (
        <link
          rel="preload"
          as="image"
          href={lcpPreloadSrc}
          {...({
            imageSrcSet: lcpPreloadSrcSet,
            imageSizes: '100vw',
            fetchPriority: 'high',
          } as React.LinkHTMLAttributes<HTMLLinkElement>)}
        />
      )}

      {/* Language */}
      <meta httpEquiv="content-language" content="nl-nl" />
      <link
        rel="alternate"
        href={site?.siteMetadata?.siteUrl}
        hrefLang="nl-nl"
      />

      {/* OpenGraph */}
      {seo.opengraphTitle && (
        <meta property="og:type" content={seo.opengraphTitle} />
      )}
      {seo.opengraphUrl && (
        <meta property="og:url" content={seo.opengraphUrl} />
      )}
      {seo.title && (
        <meta property="og:title" content={removeHTML(seo.title)} />
      )}
      {seo.metaDesc && (
        <meta property="og:description" content={removeHTML(seo.metaDesc)} />
      )}
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="nl_NL" />
    </Helmet>
  )
}

export default SEO
